<script lang="ts" setup>
import { computed } from 'vue'
import { z } from 'zod'
import { useI18n } from 'vue-i18n'

import { Carousel, CarouselContent, CarouselControlsWrapper, CarouselFooter, CarouselItem, CarouselNext, CarouselPaginationCount, CarouselPaginationDots, CarouselPrevious, Separator, CarouselCaption } from '@toni/brand-ui'

import { useImageSliderNavigation } from '../../../composables/useImageSliderNavigation'
import ImageItem from './ImageItem.vue'
import VideoPlayer from '../video/VideoPlayer.vue'
import { sliderGalleryItem } from '../../../utils/content-block-schema/slider-schema'

type GalleryItem = z.infer<typeof sliderGalleryItem>

const props = defineProps<{
  galleryItems: GalleryItem[]
}>()

const galleryItemsLength = computed(() => props.galleryItems?.length ?? 0)
const { currentIndex, onSlideChange } = useImageSliderNavigation(galleryItemsLength.value)

const currentItem = computed(() => props.galleryItems[currentIndex.value])

const { t } = useI18n()

const galleryType = computed<'images' | 'videos' | 'elements'>(() => {
  const distinctItemTypes = [...new Set(props.galleryItems.map((item) => item.paragraphType))]

  if (distinctItemTypes.length === 1) {
    return `${distinctItemTypes[0]}s`
  }

  return 'elements'
})

</script>

<template>
  <Carousel loop
            variant="extended"
            :current-slide="currentIndex"
            @select="onSlideChange">
    <CarouselContent>
      <CarouselItem
        v-for="(item, index) in galleryItems"
        :key="index"
        class="basis-full">
        <ImageItem v-if="item.paragraphType === 'image' && item.image"
                   :image="item.image" />
        <VideoPlayer v-if="item.paragraphType === 'video' && item.video"
                     :video="item.video" />
      </CarouselItem>
    </CarouselContent>
    <Separator class="mt-4 sm:mt-6" />
    <CarouselFooter>
      <CarouselPaginationCount
        :suffix="t(`mediaGallery.${galleryType}`)" />
      <CarouselControlsWrapper class="justify-end gap-4 mt-3 md:flex">
        <CarouselPrevious class="h-8 w-8 inverted" />
        <CarouselNext class="h-8 w-8 inverted" />
      </CarouselControlsWrapper>
      <CarouselPaginationDots class="block sm:hidden" />
    </CarouselFooter>
    <CarouselCaption
      v-if="currentItem?.paragraphs?.length"
      :paragraphs="currentItem?.paragraphs ?? []" />
  </Carousel>
</template>
